import { InfoCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Card,
  Checkbox,
  Col,
  Collapse,
  Popover,
  Radio,
  Row,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SAVE_APP_SETTINGS_MUTATION } from '../../../settings/graphql/mutations';
import { GET_APP_SETTINGS_QUERY } from '../../../settings/graphql/queries';
import { CHART_COLORS } from '../../constants/styles';
import {
  PhlebotomistDashboardProvider,
  usePhlebotomistDashboard,
} from '../../contexts/PhlebotomistDashboardContext';
import { getDateRangeBounds } from '../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import {
  DATE_RANGE_PREVIOUS_MAP,
  DateRangeType,
  DonationGoalData,
  PhlebotomistStats,
  TrendsIntervalSize,
} from '../../types/phlebotomist-dashboard';
import { AttemptedDonationsCard } from '../cards/AttemptedDonationsCard';
import { ComparisonMetrics } from '../cards/ComparisonMetrics';
import { DonationFailureReasonsCard } from '../cards/DonationFailureReasonsCard';
import { ProductYieldCard } from '../cards/ProductYieldCard';
import {
  AttemptedDonationsTrendChart,
  TrendDataPoint,
} from '../charts/AttemptedDonationsTrendChart';
import {
  ProductYieldTrendChart,
  ProductYieldTrendDataPoint,
} from '../charts/ProductYieldTrendChart';
import {
  SuccessRateTrendChart,
  SuccessRateTrendDataPoint,
} from '../charts/SuccessRateTrendChart';
import { CompareModeTabs } from '../filters/CompareModeTabs';
import { DateRangeSelect } from '../filters/DateRangeSelect';
import { IntervalSizeSelect } from '../filters/IntervalSizeSelect';
import { PhlebotomistSelect } from '../filters/PhlebotomistSelect';
import { RegionMultiSelect } from '../filters/RegionMultiSelect';
import { RecentDonationsTable } from '../tables/RecentDonationsTable';

const { Text } = Typography;

// Reusable dashboard pane content (used for both primary and comparison panes)
interface IDashboardPaneProps {
  stats: PhlebotomistStats | null;
  loading: boolean;
  label?: string;
  isSplitMode?: boolean;
  paneWidth?: number;
  /** Window width for responsive layout decisions */
  windowWidth?: number;
  /** Goal data for primary pane (not shown in comparison pane) */
  goalData?: DonationGoalData | null;
  /** Date range label for goal gauge */
  dateRangeLabel?: string;
  /** Phlebotomist name for goal gauge title (admin/MD viewing specific phlebotomist) */
  phlebotomistName?: string;
  /** Show debug information */
  showDebug?: boolean;
  /** Number of pending (unprocessed) donations */
  pendingDonations?: number;
  /** Visibility options for toggling sections */
  viewOptions?: IUserViewOptions;
}

const DashboardPane: FC<IDashboardPaneProps> = ({
  stats,
  loading,
  label,
  isSplitMode = false,
  paneWidth,
  windowWidth,
  goalData,
  dateRangeLabel,
  phlebotomistName,
  showDebug,
  pendingDonations = 0,
  viewOptions,
}) => {
  return (
    <div>
      {label && (
        <Text
          strong
          style={{
            display: 'block',
            textAlign: 'center',
            marginBottom: 16,
            fontSize: 16,
            color: '#0e4d8f',
          }}
        >
          {label}
        </Text>
      )}
      <Row gutter={[16, 16]}>
        {/* Stats Cards - stack vertically in split mode */}
        {(!viewOptions || viewOptions.attemptedDonations) && (
          <Col xs={24} xl={isSplitMode ? 24 : 10}>
            <AttemptedDonationsCard
              attempted={stats?.attemptedDonations || 0}
              singleDonors={stats?.singleDonors || 0}
              doubleDonors={stats?.doubleDonors || 0}
              successfulDonations={stats?.successfulDonations || 0}
              successfulSingleDonors={stats?.successfulSingleDonors || 0}
              successfulDoubleDonors={stats?.successfulDoubleDonors || 0}
              pendingDonations={pendingDonations}
              loading={loading}
              paneWidth={paneWidth}
              windowWidth={windowWidth}
              isSplitMode={isSplitMode}
              goalData={goalData}
              currentDateRangeLabel={dateRangeLabel}
              phlebotomistName={phlebotomistName}
              donations={stats?.recentDonations || []}
              showDebug={showDebug}
            />
          </Col>
        )}
        {(!viewOptions || viewOptions.productYield) && (
          <Col xs={24} xl={isSplitMode ? 24 : 14}>
            <ProductYieldCard
              stats={stats}
              loading={loading}
              paneWidth={paneWidth}
              isSplitMode={isSplitMode}
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

interface IUserViewOptions {
  attemptedDonations: boolean;
  productYield: boolean;
  donationFailureReasons: boolean;
  comparisonMetrics: boolean;
  recentDonations: boolean;
  attemptedDonationsTrend: boolean;
  productYieldTrend: boolean;
  successRateTrend: boolean;
  compareModeTabs: boolean;
  compareWithRadio: boolean;
}

const DEFAULT_VIEW_OPTIONS: IUserViewOptions = {
  attemptedDonations: true,
  productYield: true,
  donationFailureReasons: true,
  comparisonMetrics: true,
  recentDonations: true,
  attemptedDonationsTrend: true,
  productYieldTrend: true,
  successRateTrend: true,
  compareModeTabs: true,
  compareWithRadio: true,
};

const VIEW_OPTION_LABELS: { key: keyof IUserViewOptions; label: string }[] = [
  { key: 'attemptedDonations', label: 'Attempted Donations' },
  { key: 'productYield', label: 'Product Yield' },
  { key: 'donationFailureReasons', label: 'Failure Reasons' },
  { key: 'comparisonMetrics', label: 'Comparison Metrics' },
  { key: 'recentDonations', label: 'Recent Donations' },
  { key: 'compareModeTabs', label: 'Compare Mode Tabs' },
  { key: 'compareWithRadio', label: 'Compare With' },
  { key: 'attemptedDonationsTrend', label: 'Attempted Trend' },
  { key: 'productYieldTrend', label: 'Product Yield Trend' },
  { key: 'successRateTrend', label: 'Success Rate Trend' },
];

const DashboardContent: FC = () => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');
  const {
    filters,
    setFilters,
    setComparisonFilters,
    currentStats,
    officeStats,
    regionStats,
    companyStats,
    regionFullStats,
    companyFullStats,
    goalData,
    comparisonStats,
    comparisonOfficeStats,
    comparisonRegionStats,
    comparisonCompanyStats,
    comparisonGoalData,
    previousPeriodStats,
    trendData: rawTrendData,
    trendDataLoading,
    setDisplayMode,
    resetDisplayMode,
    phlebotomists,
    regions,
    loading,
    canSelectPhlebotomist,
    isCollectionTeam,
    isTeamLead,
    isPhlebotomist,
    showRegionFilter,
    showPhlebotomistFilter,
    hasRegion,
    currentUserId,
    isSimulating,
    simulationInfo,
  } = usePhlebotomistDashboard();

  // Fetch showDebug setting from backend
  const { data: settingsData } = useQuery(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: 'phlebotomist-dashboard' } },
  });
  const settingsJson = settingsData?.getAppSettings?.settingsJson as
    | Record<string, unknown>
    | undefined;
  const showDebug =
    (settingsJson as { showDebug?: boolean })?.showDebug ?? false;

  // Determine if the current user's role is allowed to see the Visibility Options accordion
  const showVisibilityOptions = useMemo(() => {
    const roles = settingsJson?.visibilityOptionsRoles as
      | { admin?: boolean; teamLead?: boolean; phlebotomist?: boolean }
      | undefined;
    if (!roles) return true; // default: show for all roles
    if (isPhlebotomist) return roles.phlebotomist !== false;
    if (isTeamLead || isCollectionTeam) return roles.teamLead !== false;
    // Admin / MedicalDirector / DVM / other admin-level roles
    return roles.admin !== false;
  }, [settingsJson, isPhlebotomist, isTeamLead, isCollectionTeam]);

  // Visibility options — per-user section toggles
  const [viewOptions, setViewOptions] =
    useState<IUserViewOptions>(DEFAULT_VIEW_OPTIONS);

  useEffect(() => {
    if (settingsJson && currentUserId && showVisibilityOptions) {
      const allUserOpts = settingsJson.userViewOptions as
        | Record<string, Partial<IUserViewOptions>>
        | undefined;
      const userOpts = allUserOpts?.[currentUserId];
      if (userOpts) setViewOptions({ ...DEFAULT_VIEW_OPTIONS, ...userOpts });
    }
    if (!showVisibilityOptions) {
      setViewOptions(DEFAULT_VIEW_OPTIONS);
    }
  }, [settingsJson, currentUserId, showVisibilityOptions]);

  const [saveSettings] = useMutation(SAVE_APP_SETTINGS_MUTATION);

  const handleViewOptionChange = useCallback(
    (key: keyof IUserViewOptions, checked: boolean) => {
      setViewOptions((prev) => {
        const newOptions = { ...prev, [key]: checked };
        if (currentUserId && settingsData?.getAppSettings?.settingsJson) {
          const existing = settingsData.getAppSettings.settingsJson as Record<
            string,
            unknown
          >;
          saveSettings({
            variables: {
              input: {
                settingsTab: 'phlebotomist-dashboard',
                settingsJson: {
                  ...existing,
                  userViewOptions: {
                    ...((existing.userViewOptions as Record<string, unknown>) ||
                      {}),
                    [currentUserId]: newOptions,
                  },
                },
              },
            },
          });
        }
        return newOptions;
      });
    },
    [currentUserId, settingsData, saveSettings]
  );

  const isSplitMode = filters.displayMode === 'split';
  const isInlineMode = filters.displayMode === 'inline';
  const isTrendsMode = filters.displayMode === 'trends';
  const isCompareMode = isSplitMode || isInlineMode;

  // Whether to show trend arrows (default view mode, not explicitly comparing)
  // Hide for self-referencing date ranges where "previous" maps to itself (always shows flat)
  const isSelfReferencingRange =
    DATE_RANGE_PREVIOUS_MAP[filters.dateRange] === filters.dateRange;
  const showTrendArrows =
    !isSplitMode &&
    !isInlineMode &&
    !isTrendsMode &&
    !filters.showRegionComparison &&
    !filters.showCompanyComparison &&
    !isSelfReferencingRange &&
    !!previousPeriodStats;

  // Side-by-side comparison: region or company radio active in normal mode
  const isSideBySide =
    !isSplitMode &&
    !isInlineMode &&
    !isTrendsMode &&
    (filters.showRegionComparison || filters.showCompanyComparison);
  const sideBySideStats: PhlebotomistStats | null = filters.showRegionComparison
    ? regionFullStats
    : filters.showCompanyComparison
    ? companyFullStats
    : null;
  const sideBySideLabel = filters.showRegionComparison
    ? regionStats?.label || 'Region'
    : filters.showCompanyComparison
    ? companyStats?.label || 'Company'
    : '';

  // In split mode: determine if comparison pane filters should be locked to primary
  // Locked when date ranges are DIFFERENT (comparing same filters across time periods)
  // Independent when date ranges are SAME (comparing different phlebotomists/regions at same time)
  const comparisonFiltersLocked = useMemo(() => {
    if (!isSplitMode || !filters.comparisonPaneFilters) return false;
    // Compare date ranges - if different, lock the filters
    return filters.dateRange !== filters.comparisonPaneFilters.dateRange;
  }, [isSplitMode, filters.dateRange, filters.comparisonPaneFilters]);

  // Track window width for split mode disable threshold (600px)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Disable split screen when window is too narrow (< 600px)
  const splitDisabled = windowWidth < 600;

  // Auto-force split mode off when window becomes too narrow
  useEffect(() => {
    if (splitDisabled && isSplitMode) {
      resetDisplayMode();
    }
  }, [splitDisabled, isSplitMode, resetDisplayMode]);

  // Sync comparison pane filters when locked (different date ranges)
  // When locked, phlebotomist and region should match the primary pane
  useEffect(() => {
    if (comparisonFiltersLocked && filters.comparisonPaneFilters) {
      const needsSync =
        filters.comparisonPaneFilters.phlebotomistId !==
          filters.phlebotomistId ||
        JSON.stringify(filters.comparisonPaneFilters.regionIds) !==
          JSON.stringify(filters.regionIds);

      if (needsSync) {
        setComparisonFilters({
          phlebotomistId: filters.phlebotomistId,
          regionIds: [...filters.regionIds],
        });
      }
    }
  }, [
    comparisonFiltersLocked,
    filters.phlebotomistId,
    filters.regionIds,
    filters.comparisonPaneFilters,
    setComparisonFilters,
  ]);

  // Measure container width using ResizeObserver for accurate responsive behavior
  // This accounts for sidebar collapse/expand state
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(element);

    // Get initial measurement
    setContainerWidth(element.getBoundingClientRect().width);

    return () => resizeObserver.disconnect();
  }, []);

  // In side-by-side mode, stack vertically when container is narrow
  const isSideBySideStacked = isSideBySide && containerWidth <= 768;
  const sideBySideColSpan = isSideBySideStacked ? 24 : 12;

  // Calculate effective pane width for responsive components
  // In split mode or side-by-side compare, each pane is roughly half the container width
  // When stacked, use full width
  const paneWidth =
    isSplitMode || (isSideBySide && !isSideBySideStacked)
      ? containerWidth * 0.5
      : containerWidth;

  // Get selected phlebotomist name for goal gauge title (admin/MD viewing specific phlebotomist)
  const selectedPhlebotomistName = useMemo(() => {
    if (!canSelectPhlebotomist || !filters.phlebotomistId) return undefined;
    const selected = phlebotomists.find(
      (ph) => ph.id === filters.phlebotomistId
    );
    return selected?.name;
  }, [canSelectPhlebotomist, filters.phlebotomistId, phlebotomists]);

  // Get selected phlebotomist name for comparison pane (split mode)
  const comparisonSelectedPhlebotomistName = useMemo(() => {
    if (
      !canSelectPhlebotomist ||
      !filters.comparisonPaneFilters?.phlebotomistId
    )
      return undefined;
    const selected = phlebotomists.find(
      (ph) => ph.id === filters.comparisonPaneFilters?.phlebotomistId
    );
    return selected?.name;
  }, [
    canSelectPhlebotomist,
    filters.comparisonPaneFilters?.phlebotomistId,
    phlebotomists,
  ]);

  // Compute resolved date bounds for debug display
  const primaryDateBounds = useMemo(
    () => getDateRangeBounds(filters.dateRange, filters.customDateRange),
    [filters.dateRange, filters.customDateRange]
  );
  const comparisonDateBounds = useMemo(
    () =>
      filters.comparisonPaneFilters?.dateRange
        ? getDateRangeBounds(
            filters.comparisonPaneFilters.dateRange,
            filters.comparisonPaneFilters.customDateRange
          )
        : null,
    [
      filters.comparisonPaneFilters?.dateRange,
      filters.comparisonPaneFilters?.customDateRange,
    ]
  );

  // Helper to get display label for date range type
  const getDateRangeLabel = useCallback(
    (dateRange: DateRangeType): string => {
      const labels: Record<DateRangeType, string> = {
        day: t('dateRange.day', 'Today'),
        dayCal: t('dateRange.dayCal', 'Today (Cal)'),
        yesterday: t('dateRange.yesterday', 'Yesterday'),
        yesterdayCal: t('dateRange.yesterdayCal', 'Yesterday (Cal)'),
        week: t('dateRange.week', 'Week'),
        lastWeek: t('dateRange.lastWeek', 'Last Week'),
        month: t('dateRange.month', 'Month'),
        lastMonth: t('dateRange.lastMonth', 'Last Month'),
        quarter: t('dateRange.quarter', 'Quarter'),
        lastQuarter: t('dateRange.lastQuarter', 'Last Quarter'),
        year: t('dateRange.year', 'Year'),
        lastYear: t('dateRange.lastYear', 'Last Year'),
        weekCal: t('dateRange.weekCal', 'Week (Cal)'),
        lastWeekCal: t('dateRange.lastWeekCal', 'Last Week (Cal)'),
        monthCal: t('dateRange.monthCal', 'Month (Cal)'),
        lastMonthCal: t('dateRange.lastMonthCal', 'Last Month (Cal)'),
        quarterCal: t('dateRange.quarterCal', 'Quarter (Cal)'),
        lastQuarterCal: t('dateRange.lastQuarterCal', 'Last Quarter (Cal)'),
        yearCal: t('dateRange.yearCal', 'Year (Cal)'),
        pYearCal: t('dateRange.pYearCal', 'Last Year (Cal)'),
        custom: t('dateRange.custom', 'Custom'),
      };
      return labels[dateRange] || dateRange;
    },
    [t]
  );

  // Get labels for inline mode
  const currentDateRangeLabel = getDateRangeLabel(filters.dateRange);
  const comparisonDateRangeLabel = filters.comparisonPaneFilters
    ? getDateRangeLabel(filters.comparisonPaneFilters.dateRange)
    : undefined;

  // Build CSV export file name
  const buildExportFileName = useCallback(
    (
      phlebotomistName: string | undefined,
      regionIds: string[],
      dateRange: DateRangeType,
      dateBounds: { start: Date; end: Date },
      customDateRange?: { start: Date; end: Date }
    ) => {
      const who = phlebotomistName
        ? phlebotomistName.replace(/\s+/g, '_')
        : 'all_phlebotomists';
      const regionSegment =
        regionIds.length === 0
          ? 'all_regions'
          : regionIds
              .map((id) => regions.find((r) => r.id === id)?.name || id)
              .join('_')
              .replace(/\s+/g, '_');
      const rangeLabel = getDateRangeLabel(dateRange).replace(/\s+/g, '_');
      const startDate = dayjs(dateBounds.start).format('YYYY-MM-DD');
      const parts = ['donations', who, regionSegment, rangeLabel, startDate];
      if (dateRange === 'custom' && customDateRange) {
        parts.push(dayjs(dateBounds.end).format('YYYY-MM-DD'));
      }
      return parts.join('-');
    },
    [getDateRangeLabel, regions]
  );

  const primaryExportFileName = useMemo(
    () =>
      buildExportFileName(
        selectedPhlebotomistName,
        filters.regionIds,
        filters.dateRange,
        primaryDateBounds,
        filters.customDateRange
      ),
    [
      buildExportFileName,
      selectedPhlebotomistName,
      filters.regionIds,
      filters.dateRange,
      primaryDateBounds,
      filters.customDateRange,
    ]
  );

  const comparisonExportFileName = useMemo(() => {
    if (!filters.comparisonPaneFilters?.dateRange || !comparisonDateBounds)
      return undefined;
    return buildExportFileName(
      comparisonSelectedPhlebotomistName,
      filters.comparisonPaneFilters.regionIds || filters.regionIds,
      filters.comparisonPaneFilters.dateRange,
      comparisonDateBounds,
      filters.comparisonPaneFilters.customDateRange
    );
  }, [
    buildExportFileName,
    comparisonSelectedPhlebotomistName,
    filters.comparisonPaneFilters?.regionIds,
    filters.comparisonPaneFilters?.dateRange,
    filters.comparisonPaneFilters?.customDateRange,
    filters.regionIds,
    comparisonDateBounds,
  ]);

  // Current interval size for trends mode (default to 'week')
  const intervalSize: TrendsIntervalSize = filters.trendsIntervalSize || 'week';

  // Transform real trend data from context into chart-compatible format
  const trendData: TrendDataPoint[] = useMemo(() => {
    if (!isTrendsMode || rawTrendData.length === 0) return [];

    return rawTrendData.map((item) => {
      const total = item.singleDonors + item.doubleDonors;
      return {
        period: item.period,
        singleDonors: item.singleDonors,
        doubleDonors: item.doubleDonors,
        total,
        sglSuccessRate: item.sglDonorsSuccessRate,
        dblSuccessRate: item.dblDonorsSuccessRate,
        totalSuccessRate: item.overallSuccessRate,
      };
    });
  }, [isTrendsMode, rawTrendData]);

  // Transform real trend data into product yield chart format
  const productYieldTrendData: ProductYieldTrendDataPoint[] = useMemo(() => {
    if (!isTrendsMode || rawTrendData.length === 0) return [];

    return rawTrendData.map((item) => ({
      period: item.period,
      ffpSglExp: item.expectedFFPSingle,
      ffpSglAct: item.actualFFPSingle,
      ffpDblExp: item.expectedFFPDouble,
      ffpDblAct: item.actualFFPDouble,
      pRbcExp: item.expectedPRBC,
      pRbcAct: item.actualPRBC,
    }));
  }, [isTrendsMode, rawTrendData]);

  // Transform real trend data into success rate chart format
  const successRateTrendData: SuccessRateTrendDataPoint[] = useMemo(() => {
    if (!isTrendsMode || rawTrendData.length === 0) return [];

    return rawTrendData.map((item) => ({
      period: item.period,
      overallSuccessRate: item.overallSuccessRate,
      prbcSuccessRate: item.prbcSuccessRate,
      sglDonorsSuccessRate: item.sglDonorsSuccessRate,
      dblDonorsSuccessRate: item.dblDonorsSuccessRate,
    }));
  }, [isTrendsMode, rawTrendData]);

  if (loading || trendDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {isSimulating && simulationInfo && (
        <div
          style={{
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 4,
            padding: '8px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <InfoCircleOutlined style={{ color: '#fa8c16' }} />
          <Typography.Text style={{ color: '#ad6800' }}>
            <strong>Simulation Active:</strong> Viewing as{' '}
            <Tag color="orange">{simulationInfo.role}</Tag>
            {simulationInfo.userName && (
              <>
                as user <strong>{simulationInfo.userName}</strong>
                {simulationInfo.officeName && (
                  <> (Office: {simulationInfo.officeName})</>
                )}
              </>
            )}
            {' \u2014 '}Configure in Settings
          </Typography.Text>
        </div>
      )}
      <Row gutter={[16, 16]} ref={containerRef}>
        {/* Visibility Options — collapsed accordion to toggle dashboard sections */}
        {showVisibilityOptions && (
          <Col span={24} style={{ paddingLeft: 0, paddingRight: 0 }}>
            <Collapse
              bordered={false}
              style={{ background: 'transparent' }}
              className="visibility-options-collapse"
            >
              <Collapse.Panel
                key="visibility"
                header={
                  <Text strong style={{ color: '#0e4d8f' }}>
                    {t('visibility.title', 'Visibility Options')}
                  </Text>
                }
                style={{ padding: 0 }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px 24px',
                  }}
                >
                  {VIEW_OPTION_LABELS.map(({ key, label }) => (
                    <Checkbox
                      key={key}
                      checked={viewOptions[key]}
                      onChange={(e) =>
                        handleViewOptionChange(key, e.target.checked)
                      }
                    >
                      {t(`visibility.${key}`, label)}
                    </Checkbox>
                  ))}
                </div>
              </Collapse.Panel>
            </Collapse>
            <style>{`
            .visibility-options-collapse .ant-collapse-header {
              padding: 0 !important;
            }
            .visibility-options-collapse .ant-collapse-content-box {
              padding: 0 0 8px 0 !important;
            }
          `}</style>
          </Col>
        )}

        {/* Mode Controls Row - hidden for Phlebotomist role (no compare modes) */}
        {viewOptions.compareModeTabs && !isPhlebotomist && (
          <Col span={24}>
            <Row gutter={[16, 8]} align="middle">
              <Col>
                <CompareModeTabs
                  displayMode={filters.displayMode}
                  onModeChange={setDisplayMode}
                  onReset={resetDisplayMode}
                  splitDisabled={splitDisabled}
                  splitDisabledTooltip={t(
                    'compareModes.splitDisabledTooltip',
                    'Split screen requires a wider display (600px+)'
                  )}
                  hideInlineCompare
                  windowWidth={windowWidth}
                />
              </Col>
              <Col flex="auto" />
            </Row>
          </Col>
        )}

        {/* Compare With radio — visible to all roles, disabled in Split/Inline/Trends */}
        {viewOptions.compareWithRadio &&
          (() => {
            const compareDisabled = isSplitMode || isInlineMode || isTrendsMode;
            const regionDisabled = !hasRegion;
            const isMobile = windowWidth < 770;

            const compareValue: 'none' | 'region' | 'company' =
              filters.showRegionComparison
                ? 'region'
                : filters.showCompanyComparison
                ? 'company'
                : 'none';

            const handleCompareChange = (
              val: 'none' | 'region' | 'company'
            ) => {
              setFilters({
                showRegionComparison: val === 'region',
                showCompanyComparison: val === 'company',
              });
            };

            const compareDisabledTooltip = compareDisabled
              ? t(
                  'filters.compareDisabledTooltip',
                  'Compare With is not available in Split, Inline, or Trends mode'
                )
              : undefined;
            const regionDisabledTooltip = regionDisabled
              ? t(
                  'filters.regionNoRegionTooltip',
                  'You need a region assigned to your account to compare with region data'
                )
              : undefined;
            const regionTooltip = t(
              'filters.regionRadioTooltip',
              'Show Region-level comparison alongside your stats'
            );
            const companyTooltip = t(
              'filters.companyRadioTooltip',
              'Show Company-wide comparison alongside your stats'
            );

            const radioGroup = (
              <Radio.Group
                value={compareDisabled ? 'none' : compareValue}
                onChange={(e) => handleCompareChange(e.target.value)}
                disabled={compareDisabled}
                className="brand-radio-group"
                style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
              >
                <Radio value="none">{t('filters.compareNone', 'None')}</Radio>
                {isMobile ? (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    <Radio value="region" disabled={regionDisabled}>
                      {t('filters.showRegion', 'Region')}
                    </Radio>
                    <Popover
                      content={
                        <span
                          style={{
                            maxWidth: 220,
                            display: 'block',
                            color: '#e8ecef',
                          }}
                        >
                          {regionDisabled
                            ? regionDisabledTooltip
                            : regionTooltip}
                        </span>
                      }
                      trigger="click"
                      placement="bottom"
                      overlayStyle={{ maxWidth: 250 }}
                      overlayInnerStyle={{
                        backgroundColor: '#0e4d8f',
                        color: '#e8ecef',
                      }}
                    >
                      <InfoCircleOutlined
                        style={{
                          color: '#0b345f',
                          fontSize: 14,
                          cursor: 'pointer',
                          marginLeft: 2,
                        }}
                      />
                    </Popover>
                  </span>
                ) : (
                  <Tooltip
                    title={
                      regionDisabled ? regionDisabledTooltip : regionTooltip
                    }
                    placement="top"
                  >
                    <Radio value="region" disabled={regionDisabled}>
                      {t('filters.showRegion', 'Region')}
                    </Radio>
                  </Tooltip>
                )}
                {isMobile ? (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    <Radio value="company">
                      {t('filters.showCompany', 'Company')}
                    </Radio>
                    <Popover
                      content={
                        <span
                          style={{
                            maxWidth: 220,
                            display: 'block',
                            color: '#e8ecef',
                          }}
                        >
                          {companyTooltip}
                        </span>
                      }
                      trigger="click"
                      placement="bottom"
                      overlayStyle={{ maxWidth: 250 }}
                      overlayInnerStyle={{
                        backgroundColor: '#0e4d8f',
                        color: '#e8ecef',
                      }}
                    >
                      <InfoCircleOutlined
                        style={{
                          color: '#0b345f',
                          fontSize: 14,
                          cursor: 'pointer',
                          marginLeft: 2,
                        }}
                      />
                    </Popover>
                  </span>
                ) : (
                  <Tooltip title={companyTooltip} placement="top">
                    <Radio value="company">
                      {t('filters.showCompany', 'Company')}
                    </Radio>
                  </Tooltip>
                )}
              </Radio.Group>
            );

            return (
              <Col span={24}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                    {t('filters.compareWith', 'Compare With')}:
                  </Text>
                  {compareDisabled ? (
                    <Tooltip title={compareDisabledTooltip} placement="top">
                      <span>{radioGroup}</span>
                    </Tooltip>
                  ) : (
                    radioGroup
                  )}
                </div>
                <style>{`
              .brand-radio-group .ant-radio-checked .ant-radio-inner {
                border-color: ${CHART_COLORS.checkbox.checked} !important;
              }
              .brand-radio-group .ant-radio-checked .ant-radio-inner::after {
                background-color: ${CHART_COLORS.checkbox.checked} !important;
              }
              .brand-radio-group .ant-radio-wrapper:hover .ant-radio-inner,
              .brand-radio-group .ant-radio:hover .ant-radio-inner {
                border-color: ${CHART_COLORS.checkbox.checked} !important;
              }
            `}</style>
              </Col>
            );
          })()}

        {/* Current Pane Filters - brand color background */}
        <Col span={24}>
          <Card
            size="small"
            style={{
              backgroundColor: CHART_COLORS.filterBar.current.background,
              borderColor: CHART_COLORS.filterBar.current.border,
            }}
          >
            {/* Current Pane label — only shown in compare mode at 900-1068px as separate row */}
            {isCompareMode &&
              containerWidth >= 900 &&
              containerWidth < 1068 && (
                <div style={{ marginBottom: 8 }}>
                  <Text
                    strong
                    style={{ color: CHART_COLORS.filterBar.current.text }}
                  >
                    {t('filters.currentPane', 'Current Pane')}:
                  </Text>
                </div>
              )}
            <div>
              {containerWidth < 900 ? (
                /* Narrow (<900px): two-column grid — Label | Dropdown, dropdowns stretch */
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: '8px 10px',
                    alignItems: 'center',
                  }}
                >
                  {isCompareMode && (
                    <Text
                      strong
                      style={{
                        color: CHART_COLORS.filterBar.current.text,
                        gridColumn: '1 / -1',
                      }}
                    >
                      {t('filters.currentPane', 'Current Pane')}:
                    </Text>
                  )}
                  <Text type="secondary">
                    {t('filters.dateRange', 'Date Range')}:
                  </Text>
                  <div>
                    <DateRangeSelect
                      value={filters.dateRange}
                      customRange={filters.customDateRange}
                      onChange={(dateRange, customDateRange) =>
                        setFilters({ dateRange, customDateRange })
                      }
                    />
                    {showDebug && (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#f31a25',
                          fontFamily: 'monospace',
                          marginTop: 2,
                        }}
                      >
                        {primaryDateBounds.start.toLocaleString()} &mdash;{' '}
                        {primaryDateBounds.end.toLocaleString()}
                      </div>
                    )}
                  </div>

                  {isTrendsMode && (
                    <>
                      <Text type="secondary">
                        {t('filters.intervalSize', 'Interval')}:
                      </Text>
                      <IntervalSizeSelect
                        value={intervalSize}
                        onChange={(value) =>
                          setFilters({ trendsIntervalSize: value })
                        }
                      />
                    </>
                  )}

                  {showRegionFilter && (
                    <>
                      <Text type="secondary">
                        {t('filters.region', 'Region')}:
                      </Text>
                      <RegionMultiSelect
                        value={filters.regionIds}
                        onChange={(regionIds) => setFilters({ regionIds })}
                        regions={regions}
                        disabled={
                          canSelectPhlebotomist && !!filters.phlebotomistId
                        }
                        disabledTooltip={t(
                          'filters.regionDisabledTooltip',
                          'Region filter is disabled when a specific phlebotomist is selected'
                        )}
                      />
                    </>
                  )}

                  {showPhlebotomistFilter && (
                    <>
                      <Text type="secondary">
                        {t('filters.phlebotomist', 'Phlebotomist')}:
                      </Text>
                      <PhlebotomistSelect
                        value={filters.phlebotomistId}
                        onChange={(phlebotomistId) =>
                          setFilters({ phlebotomistId })
                        }
                        phlebotomists={phlebotomists}
                        regionIds={filters.regionIds}
                      />
                    </>
                  )}
                </div>
              ) : (
                /* Wide (>=900px): flex row — Date:2, Region:3, Phlebotomist:3 */
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px 16px',
                    alignItems: 'center',
                  }}
                >
                  {isCompareMode && containerWidth >= 1068 && (
                    <Text
                      strong
                      style={{ color: CHART_COLORS.filterBar.current.text }}
                    >
                      {t('filters.currentPane', 'Current Pane')}:
                    </Text>
                  )}
                  <div style={{ flex: 2, minWidth: 0 }}>
                    <Text type="secondary" style={{ marginRight: 8 }}>
                      {t('filters.dateRange', 'Date Range')}:
                    </Text>
                    <DateRangeSelect
                      value={filters.dateRange}
                      customRange={filters.customDateRange}
                      onChange={(dateRange, customDateRange) =>
                        setFilters({ dateRange, customDateRange })
                      }
                    />
                    {showDebug && (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#f31a25',
                          fontFamily: 'monospace',
                          marginTop: 2,
                        }}
                      >
                        {primaryDateBounds.start.toLocaleString()} &mdash;{' '}
                        {primaryDateBounds.end.toLocaleString()}
                      </div>
                    )}
                  </div>

                  {isTrendsMode && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text type="secondary" style={{ marginRight: 8 }}>
                        {t('filters.intervalSize', 'Interval')}:
                      </Text>
                      <IntervalSizeSelect
                        value={intervalSize}
                        onChange={(value) =>
                          setFilters({ trendsIntervalSize: value })
                        }
                      />
                    </div>
                  )}

                  {showRegionFilter && (
                    <div style={{ flex: 3, minWidth: 0 }}>
                      <Text type="secondary" style={{ marginRight: 8 }}>
                        {t('filters.region', 'Region')}:
                      </Text>
                      <RegionMultiSelect
                        value={filters.regionIds}
                        onChange={(regionIds) => setFilters({ regionIds })}
                        regions={regions}
                        disabled={
                          canSelectPhlebotomist && !!filters.phlebotomistId
                        }
                        disabledTooltip={t(
                          'filters.regionDisabledTooltip',
                          'Region filter is disabled when a specific phlebotomist is selected'
                        )}
                      />
                    </div>
                  )}

                  {showPhlebotomistFilter && (
                    <div style={{ flex: 3, minWidth: 0 }}>
                      <Text type="secondary" style={{ marginRight: 8 }}>
                        {t('filters.phlebotomist', 'Phlebotomist')}:
                      </Text>
                      <PhlebotomistSelect
                        value={filters.phlebotomistId}
                        onChange={(phlebotomistId) =>
                          setFilters({ phlebotomistId })
                        }
                        phlebotomists={phlebotomists}
                        regionIds={filters.regionIds}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* Comparison Pane Filter Bar (only shown in compare mode) - brand color background */}
        {isCompareMode && filters.comparisonPaneFilters && (
          <Col span={24}>
            <Card
              size="small"
              style={{
                backgroundColor: CHART_COLORS.filterBar.comparison.background,
                borderColor: CHART_COLORS.filterBar.comparison.border,
                borderStyle: 'dashed',
              }}
            >
              {/* Comparison Pane label — only shown at 900-1068px as separate row */}
              {containerWidth >= 900 && containerWidth < 1068 && (
                <div style={{ marginBottom: 8 }}>
                  <Text
                    strong
                    style={{ color: CHART_COLORS.filterBar.comparison.text }}
                  >
                    {t('filters.comparisonPane', 'Comparison Pane')}:
                  </Text>
                </div>
              )}
              <div>
                {containerWidth < 900 ? (
                  /* Narrow (<900px): two-column grid — Label | Dropdown */
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr',
                      gap: '8px 10px',
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      strong
                      style={{
                        color: CHART_COLORS.filterBar.comparison.text,
                        gridColumn: '1 / -1',
                      }}
                    >
                      {t('filters.comparisonPane', 'Comparison Pane')}:
                    </Text>
                    <Text type="secondary">
                      {t('filters.dateRange', 'Date Range')}:
                    </Text>
                    <div>
                      <DateRangeSelect
                        value={filters.comparisonPaneFilters.dateRange}
                        customRange={
                          filters.comparisonPaneFilters.customDateRange
                        }
                        onChange={(dateRange, customDateRange) =>
                          setComparisonFilters({ dateRange, customDateRange })
                        }
                      />
                      {showDebug && comparisonDateBounds && (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#f31a25',
                            fontFamily: 'monospace',
                            marginTop: 2,
                          }}
                        >
                          {comparisonDateBounds.start.toLocaleString()} &mdash;{' '}
                          {comparisonDateBounds.end.toLocaleString()}
                        </div>
                      )}
                    </div>

                    <Text type="secondary">
                      {t('filters.region', 'Region')}:
                    </Text>
                    <div>
                      {comparisonFiltersLocked ? (
                        <Text style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                          {t('filters.lockedToPrimary', '(Locked to primary)')}
                        </Text>
                      ) : (
                        <RegionMultiSelect
                          value={filters.comparisonPaneFilters.regionIds}
                          onChange={(regionIds) =>
                            setComparisonFilters({ regionIds })
                          }
                          regions={regions}
                          disabled={
                            canSelectPhlebotomist &&
                            (isInlineMode
                              ? !!filters.phlebotomistId
                              : !!filters.comparisonPaneFilters.phlebotomistId)
                          }
                          disabledTooltip={t(
                            'filters.regionDisabledTooltip',
                            'Region filter is disabled when a specific phlebotomist is selected'
                          )}
                        />
                      )}
                    </div>

                    {canSelectPhlebotomist && isSplitMode && (
                      <>
                        <Text type="secondary">
                          {t('filters.phlebotomist', 'Phlebotomist')}:
                        </Text>
                        <div>
                          {comparisonFiltersLocked ? (
                            <Text
                              style={{ color: '#8c8c8c', fontStyle: 'italic' }}
                            >
                              {t(
                                'filters.lockedToPrimary',
                                '(Locked to primary)'
                              )}
                            </Text>
                          ) : (
                            <PhlebotomistSelect
                              value={
                                filters.comparisonPaneFilters.phlebotomistId
                              }
                              onChange={(phlebotomistId) =>
                                setComparisonFilters({ phlebotomistId })
                              }
                              phlebotomists={phlebotomists}
                              regionIds={
                                filters.comparisonPaneFilters.regionIds
                              }
                            />
                          )}
                        </div>
                      </>
                    )}

                    {canSelectPhlebotomist && isInlineMode && (
                      <>
                        <Text type="secondary">
                          {t('filters.phlebotomist', 'Phlebotomist')}:
                        </Text>
                        <Text style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                          {t('filters.lockedToPrimary', '(Locked to primary)')}
                        </Text>
                      </>
                    )}
                  </div>
                ) : (
                  /* Wide (>=900px): flex row — Date:2, Region:3, Phlebotomist:3 */
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px 16px',
                      alignItems: 'center',
                    }}
                  >
                    {containerWidth >= 1068 && (
                      <Text
                        strong
                        style={{
                          color: CHART_COLORS.filterBar.comparison.text,
                        }}
                      >
                        {t('filters.comparisonPane', 'Comparison Pane')}:
                      </Text>
                    )}
                    <div style={{ flex: 2, minWidth: 0 }}>
                      <Text type="secondary" style={{ marginRight: 8 }}>
                        {t('filters.dateRange', 'Date Range')}:
                      </Text>
                      <DateRangeSelect
                        value={filters.comparisonPaneFilters.dateRange}
                        customRange={
                          filters.comparisonPaneFilters.customDateRange
                        }
                        onChange={(dateRange, customDateRange) =>
                          setComparisonFilters({ dateRange, customDateRange })
                        }
                      />
                      {showDebug && comparisonDateBounds && (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#f31a25',
                            fontFamily: 'monospace',
                            marginTop: 2,
                          }}
                        >
                          {comparisonDateBounds.start.toLocaleString()} &mdash;{' '}
                          {comparisonDateBounds.end.toLocaleString()}
                        </div>
                      )}
                    </div>

                    <div style={{ flex: 3, minWidth: 0 }}>
                      <Text type="secondary" style={{ marginRight: 8 }}>
                        {t('filters.region', 'Region')}:
                      </Text>
                      {comparisonFiltersLocked ? (
                        <Text style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                          {t('filters.lockedToPrimary', '(Locked to primary)')}
                        </Text>
                      ) : (
                        <RegionMultiSelect
                          value={filters.comparisonPaneFilters.regionIds}
                          onChange={(regionIds) =>
                            setComparisonFilters({ regionIds })
                          }
                          regions={regions}
                          disabled={
                            canSelectPhlebotomist &&
                            (isInlineMode
                              ? !!filters.phlebotomistId
                              : !!filters.comparisonPaneFilters.phlebotomistId)
                          }
                          disabledTooltip={t(
                            'filters.regionDisabledTooltip',
                            'Region filter is disabled when a specific phlebotomist is selected'
                          )}
                        />
                      )}
                    </div>

                    {canSelectPhlebotomist && isSplitMode && (
                      <div style={{ flex: 3, minWidth: 0 }}>
                        <Text type="secondary" style={{ marginRight: 8 }}>
                          {t('filters.phlebotomist', 'Phlebotomist')}:
                        </Text>
                        {comparisonFiltersLocked ? (
                          <Text
                            style={{ color: '#8c8c8c', fontStyle: 'italic' }}
                          >
                            {t(
                              'filters.lockedToPrimary',
                              '(Locked to primary)'
                            )}
                          </Text>
                        ) : (
                          <PhlebotomistSelect
                            value={filters.comparisonPaneFilters.phlebotomistId}
                            onChange={(phlebotomistId) =>
                              setComparisonFilters({ phlebotomistId })
                            }
                            phlebotomists={phlebotomists}
                            regionIds={filters.comparisonPaneFilters.regionIds}
                          />
                        )}
                      </div>
                    )}
                    {canSelectPhlebotomist && isInlineMode && (
                      <div style={{ flex: 3, minWidth: 0 }}>
                        <Text type="secondary" style={{ marginRight: 8 }}>
                          {t('filters.phlebotomist', 'Phlebotomist')}:
                        </Text>
                        <Text style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                          {t('filters.lockedToPrimary', '(Locked to primary)')}
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </Col>
        )}

        {/* Dashboard Content - Trends Mode */}
        {isTrendsMode && (
          <>
            {/* ROW 1: Attempted Donations Trend Chart */}
            {viewOptions.attemptedDonationsTrend && (
              <Col span={24}>
                <AttemptedDonationsTrendChart
                  data={trendData}
                  loading={loading}
                  intervalSize={intervalSize}
                  paneWidth={paneWidth}
                />
              </Col>
            )}

            {/* ROW 2: Product Expected vs Actual Trend Chart */}
            {viewOptions.productYieldTrend && (
              <Col span={24}>
                <ProductYieldTrendChart
                  data={productYieldTrendData}
                  loading={loading}
                  intervalSize={intervalSize}
                  paneWidth={paneWidth}
                />
              </Col>
            )}

            {/* ROW 3: Success Rates Trend Chart */}
            {viewOptions.successRateTrend && (
              <Col span={24}>
                <SuccessRateTrendChart
                  data={successRateTrendData}
                  loading={loading}
                  intervalSize={intervalSize}
                  paneWidth={paneWidth}
                />
              </Col>
            )}
          </>
        )}

        {/* Dashboard Content - Normal Mode or Inline Mode */}
        {!isSplitMode && !isTrendsMode && (
          <>
            {/* Side-by-side pane headers */}
            {isSideBySide && (
              <Col span={24}>
                <Row gutter={[16, 0]}>
                  <Col span={sideBySideColSpan}>
                    <Text
                      strong
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        fontSize: 16,
                        color: '#0e4d8f',
                      }}
                    >
                      {selectedPhlebotomistName
                        ? `${selectedPhlebotomistName}'s Stats`
                        : canSelectPhlebotomist
                        ? t('sideBySide.filteredStats', 'Filtered Stats')
                        : t('sideBySide.yourStats', 'Your Stats')}
                    </Text>
                  </Col>
                  <Col span={sideBySideColSpan}>
                    <Text
                      strong
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        fontSize: 16,
                        color: '#0e4d8f',
                        borderLeft: isSideBySideStacked
                          ? 'none'
                          : '2px dashed #d9d9d9',
                      }}
                    >
                      {sideBySideLabel}
                    </Text>
                  </Col>
                </Row>
              </Col>
            )}

            {/* ROW 1: Stats Cards */}
            <Col span={24}>
              <Row gutter={[16, 16]}>
                {viewOptions.attemptedDonations && (
                  <Col
                    span={isSideBySide ? sideBySideColSpan : 24}
                    xl={isSideBySide ? sideBySideColSpan : 10}
                  >
                    <AttemptedDonationsCard
                      attempted={currentStats?.attemptedDonations || 0}
                      singleDonors={currentStats?.singleDonors || 0}
                      doubleDonors={currentStats?.doubleDonors || 0}
                      successfulDonations={
                        currentStats?.successfulDonations || 0
                      }
                      successfulSingleDonors={
                        currentStats?.successfulSingleDonors || 0
                      }
                      successfulDoubleDonors={
                        currentStats?.successfulDoubleDonors || 0
                      }
                      pendingDonations={currentStats?.pendingDonations ?? 0}
                      loading={loading}
                      paneWidth={paneWidth}
                      windowWidth={windowWidth}
                      isSplitMode={isSideBySide}
                      comparisonData={
                        isInlineMode && comparisonStats
                          ? {
                              attempted: comparisonStats.attemptedDonations,
                              singleDonors: comparisonStats.singleDonors,
                              doubleDonors: comparisonStats.doubleDonors,
                              successfulDonations:
                                comparisonStats.successfulDonations,
                              successfulSingleDonors:
                                comparisonStats.successfulSingleDonors,
                              successfulDoubleDonors:
                                comparisonStats.successfulDoubleDonors,
                            }
                          : undefined
                      }
                      currentDateRangeLabel={currentDateRangeLabel}
                      comparisonDateRangeLabel={
                        isInlineMode ? comparisonDateRangeLabel : undefined
                      }
                      goalData={isSideBySide ? undefined : goalData}
                      comparisonGoalData={
                        isInlineMode ? comparisonGoalData : undefined
                      }
                      phlebotomistName={selectedPhlebotomistName}
                      donations={currentStats?.recentDonations || []}
                      showDebug={showDebug}
                      previousPeriodData={
                        showTrendArrows && previousPeriodStats
                          ? {
                              attempted: previousPeriodStats.attemptedDonations,
                              singleDonors: previousPeriodStats.singleDonors,
                              doubleDonors: previousPeriodStats.doubleDonors,
                              successfulDonations:
                                previousPeriodStats.successfulDonations,
                              successfulSingleDonors:
                                previousPeriodStats.successfulSingleDonors,
                              successfulDoubleDonors:
                                previousPeriodStats.successfulDoubleDonors,
                              pendingDonations:
                                previousPeriodStats.pendingDonations,
                            }
                          : undefined
                      }
                    />
                  </Col>
                )}
                {viewOptions.attemptedDonations &&
                  isSideBySide &&
                  sideBySideStats && (
                    <Col span={sideBySideColSpan}>
                      <AttemptedDonationsCard
                        attempted={sideBySideStats.attemptedDonations || 0}
                        singleDonors={sideBySideStats.singleDonors || 0}
                        doubleDonors={sideBySideStats.doubleDonors || 0}
                        successfulDonations={
                          sideBySideStats.successfulDonations || 0
                        }
                        successfulSingleDonors={
                          sideBySideStats.successfulSingleDonors || 0
                        }
                        successfulDoubleDonors={
                          sideBySideStats.successfulDoubleDonors || 0
                        }
                        pendingDonations={sideBySideStats.pendingDonations ?? 0}
                        loading={loading}
                        paneWidth={paneWidth}
                        windowWidth={windowWidth}
                        isSplitMode
                        donations={sideBySideStats.recentDonations || []}
                        showDebug={showDebug}
                      />
                    </Col>
                  )}
                {viewOptions.productYield && !isSideBySide && (
                  <Col xs={24} xl={14}>
                    <ProductYieldCard
                      stats={currentStats}
                      loading={loading}
                      paneWidth={paneWidth}
                      comparisonStats={
                        isInlineMode ? comparisonStats : undefined
                      }
                      currentDateRangeLabel={
                        isInlineMode ? currentDateRangeLabel : undefined
                      }
                      comparisonDateRangeLabel={
                        isInlineMode ? comparisonDateRangeLabel : undefined
                      }
                      previousPeriodStats={
                        showTrendArrows ? previousPeriodStats : undefined
                      }
                    />
                  </Col>
                )}
              </Row>
            </Col>

            {/* ROW 1b: Product Yield (full width in side-by-side mode) */}
            {viewOptions.productYield && isSideBySide && (
              <Col span={24}>
                <Row gutter={[16, 16]}>
                  <Col span={sideBySideColSpan}>
                    <ProductYieldCard
                      stats={currentStats}
                      loading={loading}
                      paneWidth={paneWidth}
                      isSplitMode
                    />
                  </Col>
                  <Col span={sideBySideColSpan}>
                    <ProductYieldCard
                      stats={sideBySideStats}
                      loading={loading}
                      paneWidth={paneWidth}
                      isSplitMode
                    />
                  </Col>
                </Row>
              </Col>
            )}

            {/* ROW 2: Donation Failure Reasons */}
            <Col span={24}>
              <Row gutter={[16, 16]}>
                {viewOptions.donationFailureReasons && (
                  <Col
                    span={isSideBySide ? sideBySideColSpan : 24}
                    xl={isSideBySide ? sideBySideColSpan : 12}
                  >
                    <DonationFailureReasonsCard
                      completelyFailedDonations={
                        currentStats?.completelyFailedDonations ?? 0
                      }
                      completelyFailedDiscards={
                        currentStats?.completelyFailedDiscards || []
                      }
                      partiallyFailedDonations={
                        currentStats?.partiallyFailedDonations ?? 0
                      }
                      partiallyFailedDiscards={
                        currentStats?.partiallyFailedDiscards || []
                      }
                      attemptedDonations={currentStats?.attemptedDonations ?? 0}
                      loading={loading}
                      paneWidth={paneWidth}
                      comparisonCompletelyFailedDonations={
                        isInlineMode
                          ? comparisonStats?.completelyFailedDonations
                          : undefined
                      }
                      comparisonCompletelyFailedDiscards={
                        isInlineMode
                          ? comparisonStats?.completelyFailedDiscards
                          : undefined
                      }
                      comparisonPartiallyFailedDonations={
                        isInlineMode
                          ? comparisonStats?.partiallyFailedDonations
                          : undefined
                      }
                      comparisonPartiallyFailedDiscards={
                        isInlineMode
                          ? comparisonStats?.partiallyFailedDiscards
                          : undefined
                      }
                      comparisonAttemptedDonations={
                        isInlineMode
                          ? comparisonStats?.attemptedDonations
                          : undefined
                      }
                      showDebug={showDebug}
                      previousPeriodData={
                        showTrendArrows && previousPeriodStats
                          ? {
                              completelyFailedDonations:
                                previousPeriodStats.completelyFailedDonations ??
                                0,
                              completelyFailedDiscards:
                                previousPeriodStats.completelyFailedDiscards ||
                                [],
                              partiallyFailedDonations:
                                previousPeriodStats.partiallyFailedDonations ??
                                0,
                              partiallyFailedDiscards:
                                previousPeriodStats.partiallyFailedDiscards ||
                                [],
                              attemptedDonations:
                                previousPeriodStats.attemptedDonations ?? 0,
                            }
                          : undefined
                      }
                    />
                  </Col>
                )}
                {viewOptions.donationFailureReasons &&
                  isSideBySide &&
                  sideBySideStats && (
                    <Col span={sideBySideColSpan}>
                      <DonationFailureReasonsCard
                        completelyFailedDonations={
                          sideBySideStats.completelyFailedDonations ?? 0
                        }
                        completelyFailedDiscards={
                          sideBySideStats.completelyFailedDiscards || []
                        }
                        partiallyFailedDonations={
                          sideBySideStats.partiallyFailedDonations ?? 0
                        }
                        partiallyFailedDiscards={
                          sideBySideStats.partiallyFailedDiscards || []
                        }
                        attemptedDonations={
                          sideBySideStats.attemptedDonations ?? 0
                        }
                        loading={loading}
                        paneWidth={paneWidth}
                        showDebug={showDebug}
                      />
                    </Col>
                  )}
                {viewOptions.comparisonMetrics && !isSideBySide && (
                  <Col xs={24} xl={12}>
                    <ComparisonMetrics
                      yourStats={currentStats}
                      officeStats={officeStats}
                      regionStats={regionStats}
                      companyStats={companyStats}
                      showRegion={filters.showRegionComparison}
                      showCompany={filters.showCompanyComparison}
                      loading={loading}
                      canSelectPhlebotomist={canSelectPhlebotomist}
                      isSpecificPhlebotomistSelected={!!filters.phlebotomistId}
                      phlebotomistName={selectedPhlebotomistName}
                      paneWidth={paneWidth}
                      comparisonYourStats={
                        isInlineMode ? comparisonStats : undefined
                      }
                      comparisonOfficeStats={
                        isInlineMode ? comparisonOfficeStats : undefined
                      }
                      comparisonRegionStats={
                        isInlineMode ? comparisonRegionStats : undefined
                      }
                      comparisonCompanyStats={
                        isInlineMode ? comparisonCompanyStats : undefined
                      }
                      currentDateRangeLabel={
                        isInlineMode ? currentDateRangeLabel : undefined
                      }
                      comparisonDateRangeLabel={
                        isInlineMode ? comparisonDateRangeLabel : undefined
                      }
                      previousPeriodStats={
                        showTrendArrows ? previousPeriodStats : undefined
                      }
                    />
                  </Col>
                )}
              </Row>
            </Col>

            {/* ROW 3: Comparison Metrics (side-by-side mode) — region/company cards
              are omitted because that data is already shown in the right pane */}
            {viewOptions.comparisonMetrics && isSideBySide && (
              <Col span={24}>
                <Row gutter={[16, 16]}>
                  <Col span={sideBySideColSpan}>
                    <ComparisonMetrics
                      yourStats={currentStats}
                      officeStats={null}
                      showRegion={false}
                      showCompany={false}
                      loading={loading}
                      canSelectPhlebotomist={canSelectPhlebotomist}
                      isSpecificPhlebotomistSelected={!!filters.phlebotomistId}
                      phlebotomistName={selectedPhlebotomistName}
                      paneWidth={paneWidth}
                      isSplitMode
                    />
                  </Col>
                  <Col span={sideBySideColSpan}>
                    <ComparisonMetrics
                      yourStats={sideBySideStats}
                      officeStats={null}
                      showRegion={false}
                      showCompany={false}
                      loading={loading}
                      paneWidth={paneWidth}
                      isSplitMode
                    />
                  </Col>
                </Row>
              </Col>
            )}

            {/* ROW 4: Recent Donations Table */}
            {viewOptions.recentDonations && (
              <Col span={24}>
                <RecentDonationsTable
                  donations={currentStats?.recentDonations || []}
                  loading={loading}
                  showPhlebotomist={canSelectPhlebotomist}
                  defaultExpanded={!isCompareMode && !isSideBySide}
                  paneWidth={paneWidth}
                  exportFileName={primaryExportFileName}
                />
              </Col>
            )}
          </>
        )}

        {/* Dashboard Content - Split Screen Mode */}
        {isSplitMode && (
          <Col span={24}>
            <Row gutter={[16, 16]}>
              {/* Left Pane - Primary Data (Current Period) */}
              <Col span={12}>
                <div
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: 16,
                    height: '100%',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      marginBottom: 0,
                      fontSize: 16,
                      color: '#0e4d8f',
                    }}
                  >
                    {t('splitView.primaryPane', 'Current Period')}
                  </Text>
                  <Text
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      marginBottom: 12,
                      fontSize: 13,
                      color: '#0e4d8f',
                    }}
                  >
                    ({currentDateRangeLabel})
                  </Text>

                  <Row gutter={[16, 16]}>
                    {/* Stats Cards */}
                    <Col span={24}>
                      <DashboardPane
                        stats={currentStats}
                        loading={loading}
                        isSplitMode={true}
                        paneWidth={paneWidth}
                        windowWidth={windowWidth}
                        goalData={goalData}
                        dateRangeLabel={currentDateRangeLabel}
                        phlebotomistName={selectedPhlebotomistName}
                        showDebug={showDebug}
                        pendingDonations={currentStats?.pendingDonations ?? 0}
                        viewOptions={viewOptions}
                      />
                    </Col>

                    {/* Donation Failure Reasons */}
                    {viewOptions.donationFailureReasons && (
                      <Col span={24}>
                        <DonationFailureReasonsCard
                          completelyFailedDonations={
                            currentStats?.completelyFailedDonations ?? 0
                          }
                          completelyFailedDiscards={
                            currentStats?.completelyFailedDiscards || []
                          }
                          partiallyFailedDonations={
                            currentStats?.partiallyFailedDonations ?? 0
                          }
                          partiallyFailedDiscards={
                            currentStats?.partiallyFailedDiscards || []
                          }
                          attemptedDonations={
                            currentStats?.attemptedDonations ?? 0
                          }
                          loading={loading}
                          paneWidth={paneWidth}
                          showDebug={showDebug}
                        />
                      </Col>
                    )}

                    {/* Comparison Metrics */}
                    {viewOptions.comparisonMetrics && (
                      <Col span={24}>
                        <ComparisonMetrics
                          yourStats={currentStats}
                          officeStats={officeStats}
                          regionStats={regionStats}
                          companyStats={companyStats}
                          showRegion={filters.showRegionComparison}
                          showCompany={filters.showCompanyComparison}
                          loading={loading}
                          canSelectPhlebotomist={canSelectPhlebotomist}
                          isSpecificPhlebotomistSelected={
                            !!filters.phlebotomistId
                          }
                          phlebotomistName={selectedPhlebotomistName}
                          paneWidth={paneWidth}
                          isSplitMode
                        />
                      </Col>
                    )}

                    {/* Recent Donations Table */}
                    {viewOptions.recentDonations && (
                      <Col span={24}>
                        <RecentDonationsTable
                          donations={currentStats?.recentDonations || []}
                          loading={loading}
                          showPhlebotomist={canSelectPhlebotomist}
                          defaultExpanded={false}
                          paneWidth={paneWidth}
                          exportFileName={primaryExportFileName}
                        />
                      </Col>
                    )}
                  </Row>
                </div>
              </Col>

              {/* Right Pane - Comparison Data (Comparison Period) */}
              <Col span={12}>
                <div
                  style={{
                    border: '2px dashed #f31a25',
                    borderRadius: 8,
                    padding: 16,
                    height: '100%',
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      marginBottom: 0,
                      fontSize: 16,
                      color: '#f31a25',
                    }}
                  >
                    {t('splitView.comparisonPane', 'Comparison Period')}
                  </Text>
                  <Text
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      marginBottom: 12,
                      fontSize: 13,
                      color: '#f31a25',
                    }}
                  >
                    ({comparisonDateRangeLabel || currentDateRangeLabel})
                  </Text>

                  <Row gutter={[16, 16]}>
                    {/* Stats Cards */}
                    <Col span={24}>
                      <DashboardPane
                        stats={comparisonStats}
                        loading={loading}
                        isSplitMode={true}
                        paneWidth={paneWidth}
                        windowWidth={windowWidth}
                        goalData={comparisonGoalData}
                        dateRangeLabel={
                          comparisonDateRangeLabel || currentDateRangeLabel
                        }
                        phlebotomistName={comparisonSelectedPhlebotomistName}
                        showDebug={showDebug}
                        pendingDonations={
                          comparisonStats?.pendingDonations ?? 0
                        }
                        viewOptions={viewOptions}
                      />
                    </Col>

                    {/* Donation Failure Reasons */}
                    {viewOptions.donationFailureReasons && (
                      <Col span={24}>
                        <DonationFailureReasonsCard
                          completelyFailedDonations={
                            comparisonStats?.completelyFailedDonations ?? 0
                          }
                          completelyFailedDiscards={
                            comparisonStats?.completelyFailedDiscards || []
                          }
                          partiallyFailedDonations={
                            comparisonStats?.partiallyFailedDonations ?? 0
                          }
                          partiallyFailedDiscards={
                            comparisonStats?.partiallyFailedDiscards || []
                          }
                          attemptedDonations={
                            comparisonStats?.attemptedDonations ?? 0
                          }
                          loading={loading}
                          paneWidth={paneWidth}
                          showDebug={showDebug}
                        />
                      </Col>
                    )}

                    {/* Comparison Metrics */}
                    {viewOptions.comparisonMetrics && (
                      <Col span={24}>
                        <ComparisonMetrics
                          yourStats={comparisonStats}
                          officeStats={comparisonOfficeStats}
                          regionStats={comparisonRegionStats}
                          companyStats={comparisonCompanyStats}
                          showRegion={filters.showRegionComparison}
                          showCompany={filters.showCompanyComparison}
                          loading={loading}
                          canSelectPhlebotomist={canSelectPhlebotomist}
                          isSpecificPhlebotomistSelected={
                            !!filters.comparisonPaneFilters?.phlebotomistId
                          }
                          phlebotomistName={comparisonSelectedPhlebotomistName}
                          paneWidth={paneWidth}
                          isSplitMode
                        />
                      </Col>
                    )}

                    {/* Recent Donations Table */}
                    {viewOptions.recentDonations && (
                      <Col span={24}>
                        <RecentDonationsTable
                          donations={comparisonStats?.recentDonations || []}
                          loading={loading}
                          showPhlebotomist={canSelectPhlebotomist}
                          defaultExpanded={false}
                          paneWidth={paneWidth}
                          exportFileName={comparisonExportFileName}
                        />
                      </Col>
                    )}
                  </Row>
                </div>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </>
  );
};

export const PhlebotomistDashboard: FC = () => {
  return (
    <div style={{ padding: 24 }}>
      <PhlebotomistDashboardProvider>
        <DashboardContent />
      </PhlebotomistDashboardProvider>
    </div>
  );
};

export default PhlebotomistDashboard;
